/**
 * The console's only way to reach Broker API.
 *
 * The session lives in an HttpOnly cookie on the API host, so every request goes out with
 * credentials and no token is ever handled by this code. A 401 is not an error to display: it
 * means the session ended, and the shell sends the user back to Security.
 */

export const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:8788').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Raised on 401 so the shell can start the sign-in flow instead of rendering an error. */
export class NotSignedIn extends ApiError {
  constructor() {
    super(401, 'unauthorized', 'Session ended');
    this.name = 'NotSignedIn';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: options.body === undefined ? {} : { 'content-type': 'application/json' },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 401) throw new NotSignedIn();
  if (response.status === 204) return undefined as T;

  const body = await response.json().catch(() => undefined);
  if (!response.ok) {
    const error = (body as { error?: { code?: string; message?: string; details?: unknown } } | undefined)?.error;
    throw new ApiError(response.status, error?.code ?? 'error', error?.message ?? `Request failed (${response.status})`, error?.details);
  }
  return body as T;
}

export const startSignIn = (silent = false) => {
  const returnTo = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
  window.location.href = `${API_URL}/auth/sso/start?return_to=${returnTo}${silent ? '&prompt=none' : ''}`;
};

export async function signOut(): Promise<void> {
  const { redirect } = await api<{ redirect: string }>('/auth/sso/logout', { method: 'POST' });
  window.location.href = redirect;
}
